/**
 * Tema della console: chiaro, scuro o quello di sistema.
 *
 * La scelta è memorizzata in `localStorage` e non in `sessionStorage` — a
 * differenza del token di sessione: il tema non è un segreto, e doverlo
 * riscegliere a ogni accesso sarebbe una piccola seccatura ripetuta ogni
 * giorno.
 *
 * L'applicazione consiste nel solo `data-theme` sull'elemento radice: i colori
 * vivono nel foglio di stile, dove `color-scheme` decide quale metà di ogni
 * `light-dark()` usare. Nessun token di colore in JavaScript, così non esistono
 * due elenchi da tenere allineati.
 */

import { t } from './i18n/lingua'

export type Theme = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'censo.theme'

// Le etichette sono getter: `t()` va chiamata quando si legge l'etichetta, non
// all'import del modulo.
export const THEMES: { value: Theme; readonly label: string }[] = [
  { value: 'system', get label() { return t('Sistema') } },
  { value: 'light', get label() { return t('Chiaro') } },
  { value: 'dark', get label() { return t('Scuro') } },
]

/** Tema memorizzato, oppure "sistema" se non è mai stato scelto. */
export function readTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  } catch {
    // Modalità privata o storage negato: si continua con il tema di sistema.
    // Perdere la preferenza è meno grave che non caricare la console.
  }
  return 'system'
}

/** Applica il tema al documento e lo memorizza. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  if (theme === 'system') {
    // Rimuovere l'attributo, non impostarlo a "system": il foglio di stile
    // riconosce solo "light" e "dark", e senza attributo torna a valere il
    // `color-scheme: light dark` che segue la preferenza del sistema.
    delete root.dataset.theme
  } else {
    root.dataset.theme = theme
  }

  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // Vedi sopra: la preferenza non sopravvive alla sessione, la console sì.
  }
}
